import {
    HOTELS_UPSTREAM_BASE_URL,
    HOTEL_ITINERARY_BASE_PATH,
    HOTEL_ITINERARY_CREATE_PATH
} from '@/src/constants/hotels'
import {
    CtApplyCouponRequest,
    CtApplyCouponResponse,
    CtApplyVoucherRequest,
    CtConfirmationResponse,
    CtCouponResult,
    CtContinueToPaymentRequest,
    CtContinueToPaymentResponse,
    CtCreateItineraryRequest,
    CtCreateItineraryResponse,
    CtItineraryResponse,
    CtRemoveCouponResponse,
    CtVoucherResponse,
    CtVoucherResponseItem,
    HotelVoucherDetail
} from '@/src/types/hotels'
import { getBrandName } from '@/src/utils/buildUtils'
import { getHotelItineraryApiKey } from '@/src/utils/hotels/itineraryApiKey'
import { getCurrentLocale } from '@/src/utils/rtkQuery/getCurrentLocale'
import setCommonHeaders from '@/src/utils/rtkQuery/setCommonHeaders'
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'

/** Collapse one response voucher into the single shape the block and price transform read. */
const normalizeVoucher = (item: CtVoucherResponseItem): HotelVoucherDetail => ({
    voucherNumber: item.voucherNumber,
    pin: item.pin ?? '',
    amount: Number(item.appliedAmount) || 0,
    currency: item.currency ?? '',
    isApplied: item.status === 'S',
    errorMessage: item.errorMessage ?? ''
})

/**
 * Cleartrip hotel itinerary orchestrator — `create` on room select, then the itinerary
 * page reads it back by id.
 *
 * Deliberately does not use `createHotelBaseQuery`: that attaches the Flyin upstream's
 * contract headers (`new-native-app`, `domain: MAPP`, `api`, `device`), which mean
 * nothing to a Cleartrip service. Follows the prefixy autosuggest slice instead —
 * service-wide `setCommonHeaders` plus the two headers this API needs.
 *
 * `create` is NOT idempotent: each call books a fresh itinerary id. It is a mutation and
 * must be fired exactly once per room selection, never from a component's render or
 * effect.
 */
export const ctHotelItineraryApi = createApi({
    reducerPath: 'ctHotelItineraryApi',
    baseQuery: fetchBaseQuery({
        baseUrl: HOTELS_UPSTREAM_BASE_URL,
        prepareHeaders: headers => {
            setCommonHeaders(headers)
            // Identifies the calling brand — `cleartrip` or `flyin`, resolved from the build variant.
            headers.set('x-ct-domain', getBrandName())
            // Localized text (room type, inclusions, policies) is served off this header.
            headers.set('X-CT-LANGUAGE', getCurrentLocale())
            // Opts the orchestrator into the v2 itinerary flow these endpoints belong to.
            headers.set('X-CT-NEW-FLOW', 'true')
            return headers
        }
    }),

    tagTypes: ['CtHotelItinerary'],

    // Both checkout pages read the same itinerary; keep it cached across navigation.
    keepUnusedDataFor: 600,

    endpoints: builder => ({
        createCtItinerary: builder.mutation<CtCreateItineraryResponse, CtCreateItineraryRequest>({
            query: payload => ({
                url: HOTEL_ITINERARY_CREATE_PATH,
                method: 'POST',
                body: payload
            })
        }),

        getCtItinerary: builder.query<CtItineraryResponse, { itineraryId: string }>({
            query: ({ itineraryId }) => ({
                url: `${HOTEL_ITINERARY_BASE_PATH}/${encodeURIComponent(itineraryId)}`,
                method: 'GET'
            }),
            providesTags: (result, error, { itineraryId }) => [{ type: 'CtHotelItinerary', id: itineraryId }]
        }),

        // Post-payment. Cleartrip redirects here from its hosted payment page, so this is
        // the first read after the booking exists.
        getCtConfirmation: builder.query<CtConfirmationResponse, { itineraryId: string }>({
            query: ({ itineraryId }) => ({
                url: `${HOTEL_ITINERARY_BASE_PATH}/${encodeURIComponent(itineraryId)}/confirmation`,
                method: 'GET'
            })
        }),

        // Neither coupon call returns an updated `rateDetails`, only the discount, so the
        // applied coupon is written onto the cached itinerary and the transform subtracts
        // it. That keeps one source of truth for the price rows, the footer total and the
        // EMI figure instead of a second piece of coupon state to keep in sync.
        applyCtCoupon: builder.mutation<CtCouponResult, { itineraryId: string; couponCode: string }>({
            query: ({ itineraryId, couponCode }) => ({
                url: `${HOTEL_ITINERARY_BASE_PATH}/${encodeURIComponent(itineraryId)}/apply-coupon`,
                method: 'POST',
                body: { couponCode } satisfies CtApplyCouponRequest
            }),
            transformResponse: (response: CtApplyCouponResponse): CtCouponResult => ({
                ok: response?.status === 'SUCCESS',
                amount: Number(response?.amount) || 0,
                message: response?.message ?? ''
            }),
            async onQueryStarted({ itineraryId, couponCode }, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    if (!data.ok) return
                    dispatch(
                        ctHotelItineraryApi.util.updateQueryData('getCtItinerary', { itineraryId }, draft => {
                            draft.appliedCoupon = { code: couponCode, amount: data.amount, message: data.message }
                        })
                    )
                } catch {
                    // A rejected request leaves the itinerary untouched; the caller surfaces the error.
                }
            }
        }),

        continueCtToPayment: builder.mutation<
            CtContinueToPaymentResponse,
            { itineraryId: string; payload: CtContinueToPaymentRequest }
        >({
            query: ({ itineraryId, payload }) => {
                // Only this call is keyed; the other orchestrator endpoints are not. An
                // unmapped domain yields no key, and the header is omitted rather than sent
                // with another point of sale's value.
                const apiKey = getHotelItineraryApiKey()
                return {
                    url: `${HOTEL_ITINERARY_BASE_PATH}/${encodeURIComponent(itineraryId)}/continue-to-payment`,
                    method: 'POST',
                    ...(apiKey ? { headers: { 'x-ct-api-key': apiKey } } : {}),
                    body: payload
                }
            }
        }),

        removeCtCoupon: builder.mutation<CtCouponResult, { itineraryId: string; couponCode: string }>({
            query: ({ itineraryId, couponCode }) => ({
                url: `${HOTEL_ITINERARY_BASE_PATH}/${encodeURIComponent(itineraryId)}/remove-coupon`,
                method: 'POST',
                body: { couponCode }
            }),
            // A 200 can still carry `status: 'F'`, e.g. "Provided Coupon code - FLY is not
            // applied". Treating any 200 as removed would clear the coupon in the UI while
            // the server still holds it, understating the total.
            transformResponse: (response: CtRemoveCouponResponse): CtCouponResult => ({
                ok: response?.status === 'S',
                amount: 0,
                message: response?.errorMessage ?? ''
            }),
            async onQueryStarted({ itineraryId }, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    if (!data.ok) return
                    dispatch(
                        ctHotelItineraryApi.util.updateQueryData('getCtItinerary', { itineraryId }, draft => {
                            draft.appliedCoupon = null
                            // Every consumer falls back to the coupon stored on the document
                            // when `appliedCoupon` is absent, so clearing only the latter lets
                            // the fallback re-apply what was just removed: the block shows the
                            // coupon again, the total stays discounted, and continue-to-payment
                            // re-sends the code. Clear the stored list in the same patch so the
                            // cached itinerary consistently says "no coupon".
                            const doc = draft.ravenIntegrationAttributes?.itineraryData
                            if (doc) doc.couponInfoList = []
                        })
                    )
                } catch {
                    // Leave the applied coupon in place — it was not removed upstream.
                }
            }
        }),

        // Gift vouchers mirror the coupon endpoints: no revised `rateDetails` comes back, only
        // the redeemed amount, so the applied vouchers are written onto the cached itinerary and
        // the transform subtracts them — one source of truth for the price rows and total.
        //
        // This single call SETS the full voucher list, so it serves both add and remove: the
        // caller sends every voucher that should remain applied (the existing ones plus the new
        // one to add, or the existing ones minus the one to drop; an empty list clears all). The
        // response echoes each submitted voucher with its `status`, so the cache is replaced with
        // exactly the SUCCESS ones and the caller surfaces a FAILURE's message.
        applyHotelVouchers: builder.mutation<
            HotelVoucherDetail[],
            { itineraryId: string; payload: CtApplyVoucherRequest }
        >({
            query: ({ itineraryId, payload }) => ({
                url: `${HOTEL_ITINERARY_BASE_PATH}/${encodeURIComponent(itineraryId)}/apply/gift-voucher`,
                method: 'POST',
                body: payload
            }),
            transformResponse: (response: CtVoucherResponse): HotelVoucherDetail[] =>
                (response?.voucherDetails ?? []).map(normalizeVoucher),
            async onQueryStarted({ itineraryId }, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    dispatch(
                        ctHotelItineraryApi.util.updateQueryData('getCtItinerary', { itineraryId }, draft => {
                            draft.giftVoucherData = data.filter(voucher => voucher.isApplied)
                        })
                    )
                } catch {
                    // Rejected — leave the applied vouchers untouched.
                }
            }
        })
    })
})

export const {
    useCreateCtItineraryMutation,
    useGetCtItineraryQuery,
    useApplyCtCouponMutation,
    useRemoveCtCouponMutation,
    useApplyHotelVouchersMutation,
    useContinueCtToPaymentMutation,
    useGetCtConfirmationQuery
} = ctHotelItineraryApi

export default ctHotelItineraryApi
